import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from "@angular/core";
import { SHARED_ENVIRONMENT } from '@k12/environment';
import {
  EnrollmentApplicationProbe,
  EnrollmentApplicationResponse,
  GetEnrollmentSessionResponse,
  ProgramReview,
  ProgramTypeKey,
  Prompt,
  PromptFileAttachment,
  SubmitPrompt,
  SubmitPromptResponse
} from '@k12/models';

@Injectable({
  providedIn: 'root'
})
export class PromptService {
  private readonly http = inject(HttpClient);
  private readonly sharedEnvironment = inject(SHARED_ENVIRONMENT);
  private readonly apiUrl = this.sharedEnvironment.adminApiUrl;

  /** @deprecated Use createEnrollmentApplication + getEnrollmentSession instead. */
  public getNewEnrollment(token: string) {
    return this.http.get<SubmitPromptResponse>(`${this.apiUrl}/enrollment/${token}/newenrollment`);
  }

  public getEnrollmentApplication(programType: ProgramTypeKey, academicYearId: number) {
    const params = new HttpParams()
      .set('programType', programType)
      .set('academicYearId', academicYearId);
    return this.http.get<EnrollmentApplicationProbe>(
      `${this.apiUrl}/enrollment/application`, { params });
  }

  /**
   * Creates (or resumes) an enrollment application. `onBehalfOfPersonId` identifies the existing
   * person the application is about (required by OS/ESA+ apply; omitted for Add Student and the
   * Household Application, whose submissions create the person).
   */
  public createEnrollmentApplication(
    programType: ProgramTypeKey,
    academicYearId: number,
    onBehalfOfPersonId?: string
  ) {
    return this.http.post<EnrollmentApplicationResponse>(
      `${this.apiUrl}/enrollment/application`,
      onBehalfOfPersonId
        ? { programType, academicYearId, onBehalfOfPersonId }
        : { programType, academicYearId });
  }

  /**
   * Creates an enrollment application from an assigned task's link (OS/ESA+ renewals). Only the
   * program token and the per-instance task ids are sent: the server resolves the academic year from
   * the program's open enrollment period and the subject student from the task, so neither the year
   * nor the person id is client-supplied and a tampered link cannot retarget the enrollment.
   */
  public createEnrollmentApplicationFromTask(
    programToken: string,
    originTaskId: string,
    originSubTaskId: string
  ) {
    return this.http.post<EnrollmentApplicationResponse>(
      `${this.apiUrl}/enrollment/application`,
      { programToken, originTaskId, originSubTaskId });
  }

  public getEnrollmentSession(sessionId: string) {
    return this.http.get<GetEnrollmentSessionResponse>(
      `${this.apiUrl}/enrollment/${sessionId}/enrollmentsession`);
  }

  public submitPrompt(sessionId: string, form: SubmitPrompt) {
    return this.http.post<SubmitPromptResponse>(`${this.apiUrl}/enrollment/${sessionId}/prompt`, form);
  }

  /**
   * Uploads one file for a file_upload input. The server authorizes on the session's owner account,
   * which is what lets a registrant still in a .NEW role upload before they have a program partner.
   * Emits progress events so the drop zone can render a bar.
   */
  public uploadPromptFile(sessionId: string, inputId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<PromptFileAttachment>(
      `${this.apiUrl}/enrollment/${sessionId}/PromptFileUpload?inputId=${encodeURIComponent(inputId)}`,
      formData,
      { observe: 'events', reportProgress: true });
  }

  /** Attachment metadata already stored for an input, used to rehydrate the drop zone. */
  public getPromptFiles(sessionId: string, inputId: string) {
    return this.http.get<PromptFileAttachment[]>(
      `${this.apiUrl}/enrollment/${sessionId}/PromptFileUpload?inputId=${encodeURIComponent(inputId)}`);
  }

  public deletePromptFile(sessionId: string, inputId: string, attachmentId: string) {
    return this.http.delete<void>(
      `${this.apiUrl}/enrollment/${sessionId}/PromptFileUpload/${encodeURIComponent(attachmentId)}`
      + `?inputId=${encodeURIComponent(inputId)}`);
  }

  public getReviewPrompt(sessionId: string) {
    return this.http.get<ProgramReview>(`${this.apiUrl}/enrollment/${sessionId}/finalprompt`);
  }

  // POST, not GET: submitting is state-changing, and a GET is prefetchable by browsers and proxies.
  // With the registration re-submit relaxation on the server, a prefetch would re-dispatch the saga.
  public submitEnrollment(sessionId: string) {
    return this.http.post<void>(`${this.apiUrl}/enrollment/${sessionId}/submitEnrollment`, {});
  }

  public getPreviousPrompt(sessionId: string) {
    return this.http.get<SubmitPromptResponse>(`${this.apiUrl}/enrollment/${sessionId}/previous`);
  }

  public getSessionPromptByToken(sessionId: string, promptToken: string) {
    return this.http.get<SubmitPromptResponse>(`${this.apiUrl}/enrollment/${sessionId}/prompt/${promptToken}`);
  }

  /**
   * One prompt's structure out of a program, with no session state in it.
   *
   * Response-cached server-side under the program and prompt tokens for 24h, so one entry serves every
   * enrollee on the program. The server serves this only while the program is inside an active
   * enrollment period, which is what keeps its structure frozen; it returns 404 otherwise.
   */
  public getPromptDefinition(programToken: string, promptToken: string) {
    return this.http.get<Prompt>(
      `${this.apiUrl}/enrollment/definitions/${programToken}/prompts/${promptToken}`);
  }
}
